/**
 * Article block render registry.
 *
 * Maps each WireArticleBlock type to:
 *   - `layout` — controls how ArticleRenderer constrains the block width:
 *       'body'  — narrow reading column (max-w-prose, centered)
 *       'full'  — full container width (bleeds past text column; for thumbnail, maps, wide visuals)
 *   - `component` — the React component that renders the block
 *
 * Blocks not in this registry (classification-banner, report-details, attribution)
 * are suppressed in the reader view — they are either metadata-drawer concerns or
 * replaced by article-level components (ArticleHeader, ArticleThumbnail).
 */

import type { WireArticleBlock } from '../../../api/types'
import { ParagraphBlock }     from './ParagraphBlock'
import { HeadingBlock }       from './HeadingBlock'
import { ListBlock }          from './ListBlock'
import { SectionMarkerBlock } from './SectionMarkerBlock'
import { WarningBlock }       from './WarningBlock'
import { TextboxBlock }       from './TextboxBlock'
import { SourceListBlock }    from './SourceListBlock'
import { TableBlock }         from './TableBlock'
import { VisualBlock }        from './VisualBlock'
import { PhotoBlock }        from './PhotoBlock'
import { PhotoGroupBlock }   from './PhotoGroupBlock'
import { PhotoComparisonBlock } from './PhotoComparisonBlock'
import { VideoBlock }           from './VideoBlock'
import { AudioBlock }           from './AudioBlock'

export type BlockLayout = 'body' | 'full'

export interface BlockRegistryEntry {
  layout: BlockLayout
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component: React.ComponentType<{ block: any; renderBlock?: (b: WireArticleBlock) => React.ReactNode }>
}

export const ARTICLE_BLOCK_REGISTRY: Partial<Record<WireArticleBlock['type'], BlockRegistryEntry>> = {
  paragraph:        { layout: 'body',  component: ParagraphBlock },
  heading:          { layout: 'body',  component: HeadingBlock },
  list:             { layout: 'body',  component: ListBlock },
  'section-marker': { layout: 'body',  component: SectionMarkerBlock },
  warning:          { layout: 'body',  component: WarningBlock },
  textbox:          { layout: 'body',  component: TextboxBlock },
  'source-list':    { layout: 'body',  component: SourceListBlock },
  table:            { layout: 'body',  component: TableBlock },
  visual:           { layout: 'full',  component: VisualBlock },
  photo:              { layout: 'full',  component: PhotoBlock },
  'photo-group':      { layout: 'full',  component: PhotoGroupBlock },
  'photo-comparison': { layout: 'full',  component: PhotoComparisonBlock },
  video:              { layout: 'full',  component: VideoBlock },
  audio:              { layout: 'full',  component: AudioBlock },
  // classification-banner, report-details, attribution → suppressed (not in registry)
}
